import { useDispatch, useSelector } from "react-redux";
import * as masterServices from "../../../services/masterServices";
import * as commonAPI from "../../../services/commonServices";
import { updateCustomerList, updateLocationList } from "../../../appRedux/reducers/Common";
import { CustomNotification } from "../../../util/CustomNotification";
import FixMeLater from "../../../shared/types/fixMeLater.type";
import { General } from "../../../util/General";
import { ModuleAndIdMapping, Modules } from "../../../constants/DatabaseConstants/MasterConstant";
import { SuccessMessageConstants } from "../../../constants/MessageConstants/SuccessMessageConstants";
import useValue from "../../../hooks/useValue";

import type { AddEditMasterProps } from "../../../shared/interface/MasterManagement/masterManagementCommon.interface";
import type { RootState } from "../../../appRedux/store";

export const useAddEditMaster = (props: AddEditMasterProps) => {
  /* CONSTANTS */
  const dispatch = useDispatch();

  /* CUSTOM HOOKS */
  const { value: saving, handleDisableValue: handleDisableSaving, handleEnableValue: handleEnableSaving } = useValue(false);

  const { masterFilters } = useSelector(({ common }: RootState) => common);

  /* HANDLERS */
  /* get TopBar filters list */
  const getCustomerListAndUpdateState = async () => {
    try {
      /* getCustomerMasterList */
      const customerListResponse = await masterServices.getOneMasterListById(ModuleAndIdMapping[Modules.CUSTOMER_MASTER]);
      dispatch(updateCustomerList(customerListResponse?.["AllRecordsList"]));
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    }
  };

  const getCustomerLocationListAndUpdateState = async (customerId: number | null | undefined) => {
    try {
      if (!customerId) return;
      /* getCustomerMasterList */
      const locationListResponse = await commonAPI.getLocationListByCustomerId(customerId);
      dispatch(updateLocationList(locationListResponse?.data));
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    }
  };

  const handleSubmit = async (data: FixMeLater) => {
    try {
      /* mobile number field add +91 country code only add case */
      const formatDataMobileNo = data?.mobile_number
        ? {
            ...data,
            mobile_number: props.addRecord && data?.mobile_number ? `+91${data?.mobile_number}` : data?.mobile_number
          }
        : data;

      const formattedData = General.trimFormValues(formatDataMobileNo);

      const formData = new FormData();

      /* Iterate over the entries of the formatted data and modelId */
      for (const [key, value] of Object.entries({ ...formattedData, modelId: props.moduleId })) {
        if (value === null || value === undefined) continue;

        if (typeof value === "object") {
          if (value instanceof File) {
            /* If the value is a File, append it directly */
            formData.append(key, value);
          } else {
            /* If the value is an object (and not a File), convert it to a JSON string */
            formData.append(key, JSON.stringify(value));
          }
        } else {
          if (value === null || value === undefined) continue;

          /* For other types (string, number, etc.), append them directly */
          formData.append(key, value.toString());
        }
      }

      handleEnableSaving();

      /* add record */
      if (props.addRecord) {
        await masterServices.addMasterAPI(formData);

        props.handleAddRecord(false);
        props.handleEnableRefresh();

        /* update topBar Customer list */
        if (props.moduleId === ModuleAndIdMapping[Modules.CUSTOMER_MASTER]) {
          await getCustomerListAndUpdateState();
        }

        /* update topBar CustomerLocation list */
        if (props.moduleId === ModuleAndIdMapping[Modules.CUSTOMER_LOCATION_MASTER]) {
          await getCustomerLocationListAndUpdateState(masterFilters?.customerId);
        }

        CustomNotification.showSuccessMessage(SuccessMessageConstants.RECORD_CREATED_SUCCESSFULLY);
      } else if (props.rowId) {
        /* update record */
        await masterServices.updateMasterAPI(props.rowId, formData);

        props.handleEnableRefresh();
        props.handleClearRowIdAndActionType();

        /* update topBarCustomer list */
        if (props.moduleId === ModuleAndIdMapping[Modules.CUSTOMER_MASTER]) {
          await getCustomerListAndUpdateState();
        }

        /* update topBar CustomerLocation list */
        if (props.moduleId === ModuleAndIdMapping[Modules.CUSTOMER_LOCATION_MASTER]) {
          await getCustomerLocationListAndUpdateState(masterFilters?.customerId);
        }

        CustomNotification.showSuccessMessage(SuccessMessageConstants.RECORD_UPDATED_SUCCESSFULLY);
      } else if (props.bulkEditRecord) {
        await masterServices.bulkUpdateMasterAPI(formData);
        props.handleBulkEditRecord(false);
        props.handleEnableRefresh();
        props.handleClearRowIdAndActionType();

        CustomNotification.showSuccessMessage(SuccessMessageConstants.RECORD_UPDATED_SUCCESSFULLY);
      }
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisableSaving();
    }
  };

  return {
    saving,
    handleSubmit
  };
};
